import { supabase } from '../lib/supabase';
import type { AutomobileCatalog } from '../types/automobile';
import {
  buildCatalogFromSupabase,
  type SupabaseBrandRow,
  type SupabaseModelRow,
} from './automobileCatalogMerge';

/** Supabase birleştirmede yalnızca marka meta; catalog.json yüklenmez */
const METADATA_FALLBACK: AutomobileCatalog = {
  generatedAt: '',
  brandCount: 0,
  brandsWithModels: 0,
  modelCount: 0,
  brands: [],
};

const BRAND_SELECT =
  'id,slug,name,country_code,logo_url,updated_at,logo_focal_x,logo_focal_y,logo_zoom,is_hidden,description,founded,headquarters,website,highlights,legal_name,company_status,parent_company,successor_brand,closed_year,closure_reason,verification_level,sources';

const MODEL_SELECT =
  'id,brand_id,model_key,name,display_name,generation_code,variant_label,year_from,year_from_month,year_to,body_type,image_url,updated_at,image_generic,image_focal_x,image_focal_y,image_zoom,is_hidden,production_years';

async function fetchAllSupabaseRows<T>(
  table: string,
  select: string,
  orderColumn: string,
  pageSize = 1000,
): Promise<T[]> {
  const { count, error: countError } = await supabase
    .from(table)
    .select('*', { count: 'exact', head: true });
  if (countError) throw countError;

  const total = count ?? 0;
  if (total === 0) return [];

  const pageCount = Math.ceil(total / pageSize);
  const pages = await Promise.all(
    Array.from({ length: pageCount }, (_, page) => {
      const from = page * pageSize;
      const to = from + pageSize - 1;
      return supabase
        .from(table)
        .select(select)
        .order(orderColumn, { ascending: true })
        .range(from, to);
    }),
  );

  const out: T[] = [];
  for (const { data, error } of pages) {
    if (error) throw error;
    out.push(...((data || []) as T[]));
  }
  return out;
}

/** Admin ile aynı sıralı sayfalama; marka + model istekleri paralel */
export async function fetchAutomobileSupabaseCatalog(): Promise<AutomobileCatalog> {
  const [brandRows, modelRows] = await Promise.all([
    fetchAllSupabaseRows<SupabaseBrandRow>('automobile_brands', BRAND_SELECT, 'name'),
    fetchAllSupabaseRows<SupabaseModelRow>('automobile_models', MODEL_SELECT, 'id'),
  ]);

  return buildCatalogFromSupabase(METADATA_FALLBACK, brandRows, modelRows);
}
